import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Values } from '@lib/interfaces/form';
import toast from '@lib/components/Toast/Toast';
import { useTranslation } from 'react-i18next';
import {
  AuthenticationScopeEnum,
  AuthenticationSsoCheckQuery,
  useAuthenticationSessionCreateMutation,
  useAuthenticationSsoCheckLazyQuery,
} from 'graphql-common';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import { PATH_AUTH } from '@lib/enums/urls';
import Typography from '@lib/components/Typography/Typography';
import Form, {
  ActionsProps,
} from '@lib/components/ReactHookForm/FormContainer';
import { FormRef } from '@lib/components/ReactHookForm/types';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Trans from '@lib/components/Trans/Trans';
import LoginSsoButtons from 'components/LoginSsoButtons';
import SsoErrorModal from 'components/SsoErrorModal';
import { getLoginCombinedFields } from 'utils/authUtils';
import styles from './LoginForm.module.scss';

const { VITE_AUTH_PLATFORM_URL, VITE_API_URL } = import.meta.env;

const DEFAULT_VALUES = { login: '', password: '' };

type SsoCheckData = AuthenticationSsoCheckQuery['data'];

function LoginForm() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const formRef = useRef<FormRef>(undefined);

  const search = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('type');
    params.delete('error_code');
    const result = params.toString();
    return result ? `?${result}` : '';
  }, [searchParams]);

  const ssoErrorCode =
    searchParams.get('type') === 'sso' ? searchParams.get('error_code') : null;

  const handleSsoErrorClose = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('type');
      prev.delete('error_code');
      return prev;
    });
  }, [setSearchParams]);

  const [step, setStep] = useState<'email' | 'password'>('email');
  const [loginValue, setLoginValue] = useState('');
  const [ssoCheckResult, setSsoCheckResult] = useState<SsoCheckData | null>(
    null,
  );

  const isPasswordStep = step === 'password';

  const [
    ssoCheck,
    { loading: ssoCheckLoading, data: ssoCheckData, error: ssoCheckError },
  ] = useAuthenticationSsoCheckLazyQuery({
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (ssoCheckData) {
      setSsoCheckResult(ssoCheckData.data);
      setStep('password');
    }
  }, [ssoCheckData]);

  useEffect(() => {
    if (ssoCheckError) {
      setSsoCheckResult(null);
      setStep('password');
    }
  }, [ssoCheckError]);

  const [loginMutation, { loading: loginLoading }] =
    useAuthenticationSessionCreateMutation({
      onCompleted: () => {
        window.location.href = `${VITE_AUTH_PLATFORM_URL}/${PATH_AUTH}${search}`;
      },
      onError: (error) => {
        if (
          error.networkError &&
          'statusCode' in error.networkError &&
          error.networkError.statusCode === 429
        ) {
          toast({ content: t('too-many-requests-error') });
        } else {
          toast({ content: getErrorMessage(error, t) });
        }
      },
    });

  const handleFormSubmit = useCallback(
    (values: Values) => {
      if (step === 'email') {
        const login = (values.login as string).trim();
        setLoginValue(login);
        ssoCheck({ variables: { email: login } });
        return;
      }
      loginMutation({
        variables: {
          input: {
            uid: (values.login as string).trim(),
            password: values.password as string,
            scope: AuthenticationScopeEnum.User,
          },
        },
      });
    },
    [step, ssoCheck, loginMutation],
  );

  const handleBack = useCallback(() => {
    setStep('email');
    setSsoCheckResult(null);
  }, []);

  const handleSsoLogin = useCallback((code: string) => {
    if (!/^[a-zA-Z0-9_-]+$/.test(code)) return;
    window.location.href = `${VITE_API_URL}/sso/saml/${encodeURIComponent(code)}/init`;
  }, []);

  const fields = useMemo(
    () => getLoginCombinedFields({ t, isPasswordStep }),
    [t, isPasswordStep],
  );

  const ssoIntegrations = ssoCheckResult?.ssoIntegrations || [];
  const ssoEnabled = ssoCheckResult?.ssoEnabled || false;
  const passwordAllowed = ssoCheckResult?.passwordAllowed !== false;
  const showSsoButtons =
    isPasswordStep && ssoEnabled && ssoIntegrations.length > 0;

  const actions = useCallback(
    ({ onSubmitForm }: ActionsProps) => {
      if (!isPasswordStep) {
        return (
          <div className={styles.loginActions}>
            <Button
              type="button"
              buttonType={ButtonTypes.primaryFilled}
              buttonText={t('continue')}
              rightIcon="arrow_forward"
              fullWidth
              disabled={ssoCheckLoading || !formRef.current?.values?.login}
              isLoading={ssoCheckLoading}
              onClick={onSubmitForm}
              data-test-id="login__continue-button"
            />
          </div>
        );
      }
      return (
        <div className={styles.loginActions}>
          <div className={styles.loginActionsRow}>
            <Button
              type="button"
              buttonType={ButtonTypes.primaryOutlined}
              buttonText={t('back')}
              leftIcon="arrow_back"
              onClick={handleBack}
              data-test-id="login__back-button"
            />
            <Button
              type="submit"
              buttonType={ButtonTypes.primaryFilled}
              buttonText={t('log-in')}
              rightIcon="logout"
              disabled={loginLoading || !formRef.current?.values?.password}
              isLoading={loginLoading}
              data-test-id="login__submit-button"
            />
          </div>
        </div>
      );
    },
    [isPasswordStep, ssoCheckLoading, loginLoading, handleBack, t],
  );

  return (
    <div className={styles.loginWrap}>
      <div className={styles.headerCentered}>
        <Typography variant="h2" className={styles.titleCentered}>
          {t('welcome-to-datizy')}
        </Typography>
        <Typography variant="body" className={styles.textCentered}>
          {isPasswordStep ? (
            <Trans i18nKey="sign-in-to" values={{ email: loginValue }} />
          ) : (
            t('enter-email-to-continue')
          )}
        </Typography>
      </div>
      {showSsoButtons && (
        <LoginSsoButtons
          ssoIntegrations={ssoIntegrations}
          onSsoLogin={handleSsoLogin}
          passwordAllowed={passwordAllowed}
        />
      )}
      {isPasswordStep && !passwordAllowed ? (
        <div className={styles.loginActions}>
          <Button
            type="button"
            buttonType={ButtonTypes.primaryOutlined}
            buttonText={t('back')}
            leftIcon="arrow_back"
            onClick={handleBack}
            data-test-id="login__back-button"
          />
        </div>
      ) : (
        <Form
          autoComplete="on"
          disableAwayConfirmation
          defaultValues={DEFAULT_VALUES}
          onSubmit={handleFormSubmit}
          fields={fields}
          actions={actions}
          formRef={formRef}
        />
      )}
      <SsoErrorModal errorCode={ssoErrorCode} onClose={handleSsoErrorClose} />
    </div>
  );
}

export default LoginForm;
